/**
 * seoPages.js — Fuente única de SEO de las páginas públicas (TASK-153).
 *
 * Lo usan:
 *  - las páginas (SEOMeta) para título, descripción, canonical y schema;
 *  - scripts/prerender-seo.js, que en el build escribe un HTML por ruta con ese <head> y un
 *    resumen semántico en <noscript> (lo leen los bots que no ejecutan JavaScript: buscadores de
 *    IA, vistas previas de redes y parte de Bing), además del sitemap y llms.txt.
 *
 * Regla: solo funcionalidades que existen en el producto. uTurno es agenda + turnos online + CRM
 * de clientes (fichas por rubro, historial, etiquetas, segmentos, seguimientos), señas con
 * Mercado Pago, recordatorios por email, equipo multi-sucursal y estadísticas.
 */

export const SITE_URL = 'https://www.uturno.com';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image-premium.png`;

// Funcionalidades reales, reutilizadas en los textos de las landings
const F = {
  booking: 'Reservas online 24/7 desde tu link propio (uturno.com/tu-negocio) para Instagram, WhatsApp o Google.',
  deposits: 'Señas con Mercado Pago: el cliente paga al reservar y el dinero entra directo en tu cuenta.',
  reminders: 'Recordatorio automático por email 24 h antes, con botones para confirmar o cancelar; si cancela, el horario se libera solo.',
  crm: 'CRM de clientes: ficha con historial de turnos, notas, etiquetas y score de asistencia.',
  segments: 'Segmentos listos: clientes inactivos, nuevos, frecuentes, cumpleaños del mes y sin visitas.',
  followups: 'Seguimientos y línea de tiempo por cliente para no perder ninguna oportunidad de volver a contactarlo.',
  whatsapp: 'WhatsApp con un clic desde la agenda, con plantillas de mensaje adaptadas a tu rubro.',
  team: 'Varios profesionales y sucursales, con accesos separados para dueño, gerente y profesional.',
  commissions: 'Comisión por profesional y cálculo de lo que corresponde liquidar a fin de mes.',
  stats: 'Estadísticas de turnos, facturación y ranking de servicios y profesionales.',
  reviews: 'Reseñas públicas de tus clientes, con respuesta desde el panel.',
  importExport: 'Importá tu base de clientes desde un CSV, exportala cuando quieras y unificá duplicados.',
  walkIn: 'Turnos manuales y sobreturnos para quien llega sin reservar.',
  resources: 'Recursos físicos (canchas, boxes, salas, sillones) y clases grupales con cupo.',
  pwa: 'Se instala en el celular como una app, con notificaciones de nuevas reservas.',
};

const CORE_LINKS = [
  { href: '/precios', label: 'Precios y planes' },
  { href: '/funcionalidades', label: 'Todas las funcionalidades' },
  { href: '/crm-para-negocios', label: 'CRM para negocios de servicios' },
  { href: '/sistema-de-turnos-online', label: 'Sistema de turnos online' },
];

/** Páginas fijas del sitio (la home, precios, funcionalidades, etc.) */
export const PAGE_SEO = {
  home: {
    path: '/',
    title: 'uTurno | Agenda, Turnos Online y CRM de Clientes para tu Negocio',
    description: 'Agenda y turnos online 24/7, CRM de clientes con fichas por rubro, señas con Mercado Pago y recordatorios automáticos. Para barberías, estética, salud y más. Gratis.',
    h1: 'Agenda, turnos online y CRM de clientes en un solo lugar',
    intro: 'uTurno reemplaza la libreta, el Excel y los mensajes sueltos: tus clientes reservan solos, cobrás la seña por Mercado Pago y cada cliente queda con su ficha, su historial y sus etiquetas.',
    bullets: [F.booking, F.deposits, F.reminders, F.crm, F.segments, F.team, F.stats],
  },
  precios: {
    path: '/precios',
    title: 'Precios de uTurno | Agenda y CRM de Clientes desde $0',
    description: 'Planes en pesos, sin comisión por turno: Básico gratis, Solo con turnos ilimitados, Profesional y Multi-Sucursal. CRM incluido. 15 días gratis sin tarjeta.',
    h1: 'Precios de uTurno',
    intro: 'Tarifa fija mensual en pesos argentinos, sin comisiones por reserva. El CRM de clientes está incluido en todos los planes.',
  },
  funcionalidades: {
    path: '/funcionalidades',
    title: 'Funcionalidades | Agenda, Turnos Online y CRM de Clientes - uTurno',
    description: 'Todo lo que incluye uTurno: reservas online 24/7, CRM con fichas por rubro, segmentos y seguimientos, señas con Mercado Pago, recordatorios, equipo y estadísticas.',
    h1: 'Todo lo que incluye uTurno',
    intro: 'Agenda, reservas online y CRM de clientes pensados para negocios de servicios de Argentina.',
    bullets: [F.booking, F.crm, F.segments, F.followups, F.importExport, F.deposits, F.reminders, F.whatsapp, F.team, F.commissions, F.stats, F.reviews, F.walkIn, F.resources, F.pwa],
  },
  explorar: {
    path: '/explorar',
    title: 'Directorio de Turnos Online por Rubro y Ciudad en Argentina | uTurno',
    description: 'Buscá y reservá turnos en canchas, consultorios médicos, peluquerías, gimnasios, talleres y más en Buenos Aires, Córdoba, Rosario, Mendoza y todo el país.',
    h1: 'Turnos online por rubro y ciudad',
    intro: 'Directorio de rubros y ciudades de Argentina con reserva online.',
  },
  terms: {
    path: '/terms',
    title: 'Términos y Condiciones de Uso | uTurno',
    description: 'Consulta los términos legales, condiciones de suscripción SaaS, cobro de señas con Mercado Pago y políticas de reseñas en uTurno.',
    h1: 'Términos y Condiciones de Uso',
  },
  privacy: {
    path: '/privacy',
    title: 'Política de Privacidad y Protección de Datos | uTurno',
    description: 'Conozca cómo uTurno protege sus datos personales conforme a la Ley 25.326. Derechos ARCO, seguridad y transparencia en nuestra plataforma SaaS.',
    h1: 'Política de Privacidad',
  },
};

/**
 * Landings por intención de búsqueda. Cada una con texto propio (no plantillas repetidas):
 * términos genéricos (CRM, sistema de turnos, turnero, agenda online) y profesiones que no
 * tenían página.
 */
export const KEYWORD_LANDINGS = [
  {
    slug: 'crm-para-negocios',
    title: 'CRM para Negocios de Servicios con Agenda y Turnos | uTurno',
    description: 'CRM de clientes con agenda integrada: fichas por rubro, historial, etiquetas, segmentos, seguimientos y turnos online. Hecho para Argentina. Plan gratis.',
    eyebrow: 'CRM + Agenda',
    h1: 'El CRM para negocios que trabajan con turnos',
    intro: 'La mayoría de los CRM no saben qué es un turno, y la mayoría de las agendas no saben quién es tu cliente. uTurno junta las dos cosas: cada reserva alimenta la ficha del cliente, y desde la ficha ves cuándo volvió, cuánto gastó y a quién conviene llamar.',
    pains: [
      'Los datos de tus clientes están repartidos entre el celular, un Excel y la memoria.',
      'No sabés quién dejó de venir hasta que ya es tarde para recuperarlo.',
      'Cada profesional anota distinto y la información se pierde cuando alguien se va.',
    ],
    features: [F.crm, F.segments, F.followups, F.importExport, F.whatsapp, F.booking, F.stats],
    faqs: [
      { q: '¿Qué diferencia hay entre uTurno y un CRM tradicional?', a: 'uTurno nace de la agenda: cada turno reservado, atendido, cancelado o ausente queda en la ficha del cliente automáticamente. No tenés que cargar nada dos veces.' },
      { q: '¿Puedo traer mis clientes actuales?', a: 'Sí. Importás un archivo CSV con nombre, teléfono y email, y si un cliente quedó repetido lo unificás con su historial completo.' },
      { q: '¿El CRM tiene costo extra?', a: 'No. El CRM de clientes está incluido en todos los planes, también en el gratuito.' },
    ],
    related: ['sistema-de-turnos-online', 'agenda-online', 'software-para-peluquerias'],
  },
  {
    slug: 'sistema-de-turnos-online',
    title: 'Sistema de Turnos Online con Señas y CRM | uTurno',
    description: 'Sistema de turnos online para negocios y profesionales: reservas 24/7, señas con Mercado Pago, recordatorios automáticos y CRM de clientes. Empezá gratis.',
    eyebrow: 'Turnos online',
    h1: 'Sistema de turnos online para tu negocio',
    intro: 'Publicá tu agenda en un link y dejá que tus clientes reserven solos, a cualquier hora. uTurno controla la disponibilidad real de cada profesional, cobra la seña si la necesitás y le recuerda el turno al cliente un día antes.',
    pains: [
      'Pasás el día respondiendo "¿tenés lugar mañana?".',
      'Los turnos que no se presentan son horas pagas que no cobrás.',
      'Dos personas reservan el mismo horario porque la agenda está en papel.',
    ],
    features: [F.booking, F.deposits, F.reminders, F.walkIn, F.team, F.crm, F.pwa],
    faqs: [
      { q: '¿Mis clientes tienen que descargar una app?', a: 'No. Reservan desde el navegador del celular o la computadora, entrando a tu link.' },
      { q: '¿Cuánto cuesta?', a: 'Hay un plan gratuito para empezar y planes pagos en pesos, sin comisión por turno. Todos tienen 15 días de prueba sin tarjeta.' },
      { q: '¿Puedo cobrar una seña al reservar?', a: 'Sí. Conectás tu cuenta de Mercado Pago y definís el monto de seña por servicio.' },
    ],
    related: ['turnero-online', 'agenda-online', 'crm-para-negocios'],
  },
  {
    slug: 'turnero-online',
    title: 'Turnero Online Gratis para Negocios | uTurno',
    description: 'Turnero online para dar turnos por internet: tus clientes eligen día y hora, reciben confirmación por email y vos ves todo en una agenda. Plan gratis.',
    eyebrow: 'Turnero digital',
    h1: 'Turnero online: tus clientes sacan turno solos',
    intro: 'Un turnero digital que funciona las 24 horas: el cliente elige servicio, profesional, día y hora, y el turno aparece en tu agenda al instante. Vos solo atendés.',
    pains: [
      'Dar turnos por teléfono te interrumpe mientras trabajás.',
      'Los mensajes a la noche quedan sin responder y el cliente reserva en otro lado.',
      'No tenés registro de quién vino y quién faltó.',
    ],
    features: [F.booking, F.reminders, F.deposits, F.crm, F.walkIn, F.pwa],
    faqs: [
      { q: '¿El turnero es gratis?', a: 'Sí, el plan Básico es gratuito, con 1 profesional y hasta 50 turnos por mes.' },
      { q: '¿Puedo poner el turnero en mi Instagram?', a: 'Sí. Te damos un link propio para la bio de Instagram, tu perfil de Google o para mandar por WhatsApp.' },
      { q: '¿Cómo se entera el cliente de que tiene turno?', a: 'Recibe la confirmación por email al reservar y un recordatorio 24 horas antes.' },
    ],
    related: ['sistema-de-turnos-online', 'agenda-online', 'software-para-peluquerias'],
  },
  {
    slug: 'agenda-online',
    title: 'Agenda Online para Profesionales con Reservas y CRM | uTurno',
    description: 'Agenda online para profesionales independientes y equipos: reservas 24/7, vista diaria y mensual, recordatorios automáticos y ficha de cada cliente.',
    eyebrow: 'Agenda digital',
    h1: 'Agenda online para profesionales',
    intro: 'Tu agenda en el celular y en la computadora, siempre actualizada: ves el día o el mes completo, movés turnos, cargás sobreturnos y abrís la ficha del cliente con un toque.',
    pains: [
      'La agenda de papel no te avisa cuando alguien cancela.',
      'Compartir la agenda con tu equipo es un caos de capturas de pantalla.',
      'No podés ver tu agenda cuando no estás en el local.',
    ],
    features: [F.booking, F.team, F.walkIn, F.reminders, F.crm, F.stats, F.pwa],
    faqs: [
      { q: '¿Sirve si trabajo solo?', a: 'Sí. El plan Básico es gratuito para un profesional, y podés sumar equipo cuando crezcas.' },
      { q: '¿Cada profesional puede ver solo su agenda?', a: 'Sí. Cada profesional tiene su propio acceso y ve sus turnos; el dueño y los gerentes ven la agenda completa de su sucursal.' },
      { q: '¿Funciona en el celular?', a: 'Sí, y se puede instalar como una app para recibir avisos de nuevas reservas.' },
    ],
    related: ['sistema-de-turnos-online', 'turnero-online', 'crm-para-negocios'],
  },
  {
    slug: 'software-para-peluquerias',
    title: 'Software para Peluquerías: Turnos Online y CRM | uTurno',
    description: 'Software para peluquerías y salones: turnos online 24/7, ficha de cada clienta con color y tratamientos, señas con Mercado Pago y agenda por estilista.',
    eyebrow: 'Peluquerías',
    h1: 'Software para peluquerías y salones',
    intro: 'Cada estilista con su agenda, cada clienta con su ficha: fórmula de color, tipo de cabello y alergias a tinturas a mano en el próximo turno. Las reservas entran solas desde Instagram.',
    pains: [
      'Te olvidás qué fórmula de color usaste la última vez.',
      'Los turnos largos de color quedan vacíos porque la clienta no vino.',
      'Las clientas que no vuelven hace meses pasan desapercibidas.',
    ],
    features: [F.booking, F.deposits, F.crm, F.segments, F.whatsapp, F.team, F.reminders],
    faqs: [
      { q: '¿Puedo guardar la fórmula de color de cada clienta?', a: 'Sí. La ficha de estilo guarda tipo de cabello, alergias a tinturas y notas de cada visita.' },
      { q: '¿Puedo cobrar seña solo en los servicios largos?', a: 'Sí. La seña se configura por servicio: podés pedirla en color y alisados y no en un corte.' },
      { q: '¿Cómo recupero clientas que no vuelven?', a: 'El segmento "inactivos" te muestra quién no reserva hace tiempo, y le escribís por WhatsApp con un clic.' },
    ],
    related: ['software-para-barberias', 'software-para-estetica', 'software-para-unas-y-manicura'],
    relatedExternal: [{ href: '/software-para-barberias', label: 'Software para barberías' }],
  },
  {
    slug: 'software-para-psicologos',
    title: 'Software para Psicólogos: Agenda de Sesiones y Pacientes | uTurno',
    description: 'Agenda online para psicólogos y terapeutas: sesiones 24/7, recordatorios automáticos, ficha de cada paciente con notas privadas y cobro de seña.',
    eyebrow: 'Psicología',
    h1: 'Agenda online para psicólogos y terapeutas',
    intro: 'Tus pacientes reservan su sesión sin escribirte, reciben un recordatorio el día anterior y vos tenés la ficha de cada uno con sus datos y notas, visibles solo para vos y tu equipo.',
    pains: [
      'Coordinar horarios por mensaje te quita tiempo entre sesiones.',
      'Las sesiones canceladas a último momento no se recuperan.',
      'Las notas de cada paciente están en cuadernos distintos.',
    ],
    features: [F.booking, F.reminders, F.deposits, F.crm, F.followups, F.stats],
    faqs: [
      { q: '¿Las notas de los pacientes son privadas?', a: 'Sí. Las fichas solo las ve tu cuenta y las personas de tu equipo con acceso; el paciente no las ve.' },
      { q: '¿Puedo atender sesiones online?', a: 'Sí. Podés crear servicios de sesión online y presencial con duraciones distintas.' },
      { q: '¿Puedo pedir un pago anticipado?', a: 'Sí, con Mercado Pago: la sesión se confirma cuando el paciente paga la seña.' },
    ],
    related: ['software-para-nutricionistas', 'software-para-medicos', 'agenda-online'],
  },
  {
    slug: 'software-para-odontologos',
    title: 'Software para Odontólogos: Turnos y Ficha del Paciente | uTurno',
    description: 'Sistema de turnos para consultorios odontológicos: agenda por profesional, ficha con obra social y alergias, recordatorios automáticos y señas.',
    eyebrow: 'Odontología',
    h1: 'Sistema de turnos para consultorios odontológicos',
    intro: 'Agenda por odontólogo y por sillón, ficha del paciente con obra social, número de afiliado y alergias marcadas como críticas, y recordatorio automático para bajar las ausencias.',
    pains: [
      'Los pacientes faltan y el sillón queda vacío.',
      'La secretaria pasa la mañana confirmando turnos por teléfono.',
      'Los datos de obra social se piden en cada visita.',
    ],
    features: [F.booking, F.reminders, F.crm, F.resources, F.team, F.deposits],
    faqs: [
      { q: '¿Puedo registrar la obra social del paciente?', a: 'Sí. La ficha médica incluye obra social o prepaga, número de afiliado, alergias y antecedentes.' },
      { q: '¿Sirve para un consultorio con varios profesionales?', a: 'Sí. Cada profesional tiene su agenda y su acceso; también podés organizar por sillón o box.' },
      { q: '¿El paciente puede cancelar solo?', a: 'Sí, desde el email de recordatorio, y el horario queda libre para otro paciente.' },
    ],
    related: ['software-para-medicos', 'software-para-kinesiologos', 'software-para-psicologos'],
  },
  {
    slug: 'software-para-kinesiologos',
    title: 'Software para Kinesiólogos y Fisioterapeutas | uTurno',
    description: 'Turnos online para kinesiología y fisioterapia: series de sesiones, agenda por box, ficha del paciente con obra social y recordatorios automáticos.',
    eyebrow: 'Kinesiología',
    h1: 'Turnos online para kinesiología y fisioterapia',
    intro: 'Organizá los tratamientos de muchas sesiones sin perder a nadie en el camino: cada paciente con su ficha, su historial de sesiones y un recordatorio antes de cada turno.',
    pains: [
      'Los pacientes abandonan el tratamiento a mitad de camino.',
      'Los boxes se superponen cuando la agenda se lleva a mano.',
      'No sabés cuántas sesiones le quedan a cada paciente.',
    ],
    features: [F.booking, F.crm, F.followups, F.resources, F.reminders, F.stats],
    faqs: [
      { q: '¿Puedo ver cuántas sesiones hizo cada paciente?', a: 'Sí. La ficha muestra el historial completo de turnos atendidos, cancelados y ausentes.' },
      { q: '¿Puedo organizar por box o camilla?', a: 'Sí. Cargás los boxes como recursos y el sistema evita superposiciones.' },
      { q: '¿Cómo retomo a quien dejó el tratamiento?', a: 'Con los segmentos de inactivos y los seguimientos, ves a quién llamar y le escribís con un clic.' },
    ],
    related: ['software-para-odontologos', 'software-para-medicos', 'software-para-entrenadores-personales'],
  },
  {
    slug: 'software-para-nutricionistas',
    title: 'Software para Nutricionistas: Agenda y Ficha de Pacientes | uTurno',
    description: 'Agenda online para nutricionistas: consultas y controles 24/7, ficha de cada paciente, seguimientos, recordatorios automáticos y cobro de seña.',
    eyebrow: 'Nutrición',
    h1: 'Agenda online para nutricionistas',
    intro: 'Los controles periódicos son la clave de un plan nutricional. uTurno te muestra quién tiene que volver, guarda la ficha de cada paciente y le recuerda el turno un día antes.',
    pains: [
      'Los pacientes no vuelven al control y el plan se abandona.',
      'Anotás pesos y medidas en planillas sueltas.',
      'No sabés qué pacientes están activos.',
    ],
    features: [F.booking, F.crm, F.followups, F.segments, F.reminders, F.deposits],
    faqs: [
      { q: '¿Puedo agendar el próximo control al terminar la consulta?', a: 'Sí, cargás el turno desde la agenda en segundos y al paciente le llega la confirmación por email.' },
      { q: '¿Puedo marcar recordatorios para llamar a un paciente?', a: 'Sí. Los seguimientos te avisan a quién contactar y cuándo.' },
      { q: '¿Atiendo online y presencial?', a: 'Sí, con servicios distintos para cada modalidad.' },
    ],
    related: ['software-para-psicologos', 'software-para-medicos', 'software-para-entrenadores-personales'],
  },
  {
    slug: 'software-para-medicos',
    title: 'Software para Consultorios Médicos y Recetas Digitales | uTurno',
    description: 'Sistema de turnos para consultorios médicos: agenda por profesional, ficha con obra social y antecedentes, recetas con código verificable y recordatorios.',
    eyebrow: 'Consultorios médicos',
    h1: 'Turnos y fichas para consultorios médicos',
    intro: 'Agenda online por médico, ficha del paciente con obra social, alergias, antecedentes y medicación habitual, y recetas emitidas desde el sistema con un código que la farmacia puede verificar.',
    pains: [
      'El teléfono del consultorio no para y los pacientes no consiguen turno.',
      'Las ausencias dejan huecos que nadie aprovecha.',
      'Los datos críticos del paciente no están a mano durante la consulta.',
    ],
    features: [F.booking, F.reminders, F.crm, F.team, F.stats, 'Recetas con código de verificación público para farmacias y pacientes.'],
    faqs: [
      { q: '¿Puedo emitir recetas?', a: 'Sí. Las recetas se generan desde la ficha del paciente y tienen un código que se verifica en uturno.com/verificar-receta.' },
      { q: '¿Qué datos guarda la ficha médica?', a: 'Obra social, número de afiliado, grupo sanguíneo, alergias, antecedentes, medicación habitual y contacto de emergencia.' },
      { q: '¿Sirve para un centro con varias especialidades?', a: 'Sí. Cada profesional tiene su agenda y podés trabajar con varias sucursales.' },
    ],
    related: ['software-para-odontologos', 'software-para-kinesiologos', 'software-para-psicologos'],
    relatedExternal: [{ href: '/software-para-salud', label: 'Software para salud' }],
  },
  {
    slug: 'software-para-unas-y-manicura',
    title: 'Software para Manicuras y Nail Bars: Turnos Online | uTurno',
    description: 'Turnos online para manicuras, nail bars y estudios de uñas: reservas 24/7 desde Instagram, ficha de cada clienta, señas con Mercado Pago y recordatorios.',
    eyebrow: 'Uñas y manicura',
    h1: 'Turnos online para manicuras y nail bars',
    intro: 'Tus clientas reservan desde el link de tu Instagram, pagan la seña y reciben un recordatorio. En su ficha quedan el diseño, el color y las alergias a productos.',
    pains: [
      'Respondés mensajes de turnos entre clienta y clienta.',
      'Una ausencia en un servicio de dos horas es medio día perdido.',
      'No te acordás qué diseño se hizo cada clienta.',
    ],
    features: [F.booking, F.deposits, F.reminders, F.crm, F.segments, F.whatsapp],
    faqs: [
      { q: '¿Puedo cobrar seña?', a: 'Sí, por Mercado Pago y por servicio. El turno se confirma cuando la clienta paga.' },
      { q: '¿Queda guardado el diseño de cada clienta?', a: 'Sí, en la ficha estética: preferencias de uñas, color, tratamientos previos y alergias.' },
      { q: '¿Sirve si trabajo sola?', a: 'Sí, el plan Básico es gratuito para una profesional.' },
    ],
    related: ['software-para-spa-y-masajes', 'software-para-peluquerias', 'software-para-estetica'],
    relatedExternal: [{ href: '/software-para-estetica', label: 'Software para centros de estética' }],
  },
  {
    slug: 'software-para-spa-y-masajes',
    title: 'Software para Spa y Masajes: Reservas y Clientes | uTurno',
    description: 'Sistema de reservas para spa, masajistas y centros de bienestar: agenda por cabina, ficha con contraindicaciones, señas con Mercado Pago y recordatorios.',
    eyebrow: 'Spa y bienestar',
    h1: 'Reservas online para spa y masajes',
    intro: 'Organizá cabinas y terapeutas en una sola agenda, cobrá la seña al reservar y tené a mano las contraindicaciones de cada cliente antes de empezar la sesión.',
    pains: [
      'Las cabinas se superponen cuando la agenda es compartida.',
      'Las contraindicaciones del cliente se preguntan cada vez.',
      'Los clientes de temporada no vuelven a reservar.',
    ],
    features: [F.booking, F.resources, F.deposits, F.crm, F.segments, F.reminders],
    faqs: [
      { q: '¿Puedo organizar por cabina?', a: 'Sí. Cargás cabinas o salas como recursos y el sistema evita dos turnos en el mismo lugar.' },
      { q: '¿Se guardan las contraindicaciones?', a: 'Sí, en la ficha del cliente y marcadas como datos críticos.' },
      { q: '¿Puedo mandar promociones a clientes que no vienen hace tiempo?', a: 'Filtrás el segmento de inactivos y les escribís por WhatsApp con tus plantillas.' },
    ],
    related: ['software-para-unas-y-manicura', 'software-para-peluquerias', 'crm-para-negocios'],
    relatedExternal: [{ href: '/software-para-estetica', label: 'Software para centros de estética' }],
  },
  {
    slug: 'software-para-tatuadores',
    title: 'Software para Tatuadores: Turnos, Señas y Clientes | uTurno',
    description: 'Agenda online para estudios de tatuajes: sesiones con seña obligatoria por Mercado Pago, ficha de cada cliente, varios tatuadores y recordatorios.',
    eyebrow: 'Estudios de tatuaje',
    h1: 'Agenda y señas para estudios de tatuajes',
    intro: 'Las sesiones largas necesitan seña. Con uTurno el cliente reserva y paga la seña por Mercado Pago en el mismo paso, y cada tatuador maneja su propia agenda.',
    pains: [
      'Un cliente que no viene es una tarde entera perdida.',
      'Cobrar la seña por transferencia y controlarla es un trámite.',
      'Las sesiones de un mismo tatuaje se pierden entre mensajes.',
    ],
    features: [F.deposits, F.booking, F.team, F.crm, F.reminders, F.reviews],
    faqs: [
      { q: '¿La seña es obligatoria?', a: 'Vos decidís por servicio. Si la activás, el turno se confirma solo cuando se acredita el pago.' },
      { q: '¿Qué pasa si el cliente no paga la seña?', a: 'La reserva se libera sola a los 15 minutos para que otro cliente pueda tomar el horario.' },
      { q: '¿Cada tatuador tiene su agenda?', a: 'Sí, con su propio acceso y sus servicios.' },
    ],
    related: ['software-para-peluquerias', 'crm-para-negocios', 'sistema-de-turnos-online'],
  },
  {
    slug: 'software-para-entrenadores-personales',
    title: 'Software para Entrenadores Personales y Clases | uTurno',
    description: 'Reservas online para entrenadores personales, yoga y pilates: clases grupales con cupo, sesiones individuales, ficha de cada alumno y recordatorios.',
    eyebrow: 'Entrenamiento y clases',
    h1: 'Reservas para entrenadores, yoga y pilates',
    intro: 'Publicá tus clases con cupo y tus sesiones individuales en un solo link. Los alumnos se anotan solos, el cupo se cierra al llenarse y cada alumno queda con su ficha.',
    pains: [
      'Las listas de alumnos por WhatsApp se desordenan.',
      'Se anotan más personas de las que entran en la clase.',
      'No sabés qué alumnos dejaron de venir.',
    ],
    features: [F.resources, F.booking, F.crm, F.segments, F.deposits, F.reminders],
    faqs: [
      { q: '¿Puedo tener clases grupales con cupo?', a: 'Sí. Definís la capacidad y la inscripción se cierra sola al completarse.' },
      { q: '¿Puedo cobrar la clase por adelantado?', a: 'Sí, con seña o pago por Mercado Pago al reservar.' },
      { q: '¿Veo la asistencia de cada alumno?', a: 'Sí, en su ficha: turnos atendidos, ausentes y cancelados.' },
    ],
    related: ['software-para-kinesiologos', 'software-para-nutricionistas', 'agenda-online'],
    relatedExternal: [{ href: '/academias', label: 'Software para academias' }],
  },
  {
    slug: 'software-para-peluquerias-caninas',
    title: 'Software para Peluquerías Caninas y Grooming | uTurno',
    description: 'Turnos online para peluquería canina y grooming: ficha de cada mascota con raza, peso y alergias, recordatorios automáticos y señas con Mercado Pago.',
    eyebrow: 'Grooming',
    h1: 'Turnos online para peluquería canina',
    intro: 'El dueño reserva el baño y corte desde el celular, y en la ficha de la mascota quedan la raza, el peso, las alergias y cómo le gusta el corte.',
    pains: [
      'Los dueños olvidan el turno y el día queda con huecos.',
      'Los datos de cada mascota se piden en cada visita.',
      'No sabés cuándo le toca volver a cada perro.',
    ],
    features: [F.booking, F.crm, F.reminders, F.deposits, F.segments, F.whatsapp],
    faqs: [
      { q: '¿Puedo guardar varias mascotas por dueño?', a: 'Sí. La ficha de mascotas guarda nombre, especie, raza, peso, alergias y vacunas.' },
      { q: '¿El dueño recibe recordatorio?', a: 'Sí, por email 24 horas antes, con botones para confirmar o cancelar.' },
      { q: '¿Sirve también para una veterinaria?', a: 'Sí, y tenemos una página específica para veterinarias.' },
    ],
    related: ['software-para-peluquerias', 'crm-para-negocios', 'turnero-online'],
    relatedExternal: [{ href: '/veterinaria', label: 'Software para veterinarias' }],
  },
];

export const getKeywordLanding = (slug) => KEYWORD_LANDINGS.find((l) => l.slug === slug) || null;

export const buildKeywordLandingSchema = (landing) => [
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `uTurno — ${landing.eyebrow}`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, Android, iOS',
    url: `${SITE_URL}/${landing.slug}`,
    description: landing.description,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'ARS' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: landing.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: landing.eyebrow, item: `${SITE_URL}/${landing.slug}` },
    ],
  },
];

export { CORE_LINKS };
